import pick from "lodash/pick";

import { fetcher, STANDARD_NEXT_FETCH_OPTIONS } from "src/utils/fetcher";
import isBrowser from "src/utils/isBrowser";

interface Images {
  url_16x16: string;
  url_32x32: string;
  url_64x64: string;
  url_128x128: string;
}

export interface Action {
  key: string;
  type: string;
  name: string;
  description: string;
  meta: {
    hook?: {
      is_notification: boolean;
      is_subscription: boolean;
      is_polling_notification: boolean;
      is_pollable: boolean;
      url: string | null;
      directions: string | null;
    };
  };
}

interface AppImplementation {
  selected_api: string;
  actions: Action[];
  name: string;
  slug: string;
  images: Images;
  current_implementation: string;
}

export interface AppImplementationResponse {
  results: AppImplementation[];
}

type Props = {
  selectedApis: string[];
  cache?: boolean;
};

export const getImplementationDetails = async ({
  selectedApis,
  cache = false,
}: Props) => {
  if (selectedApis.length === 0) return [];
  const url = new URL(
    `/api/v4/implementations`,
    isBrowser()
      ? window.location.origin
      : process.env.NEXT_PUBLIC_ZAPIER_ORIGIN,
  );
  url.search = new URLSearchParams({
    selected_apis: selectedApis.join(","),
    limit: String(selectedApis.length),
  }).toString();

  const response = await fetcher<AppImplementationResponse>(url, {
    next: cache ? STANDARD_NEXT_FETCH_OPTIONS : undefined,
  });

  // NOTE: We only pick certain fields to dramatically reduce the JSON payload sent to the client.
  const results = response.results.map((result) => {
    return {
      ...pick(result, [
        "name",
        "slug",
        "images",
        "selected_api",
        "current_implementation",
      ]),
      actions: result.actions.map((action) =>
        pick(action, ["key", "name", "description", "meta.hook", "type"]),
      ) as Action[],
    };
  });

  return results;
};
